// The file check the upload page runs in the browser before anything is sent (call map section 9,
// wf-teacher-upload 2–3). POST /api/lessons repeats it with the same rules and the same messages.
// Rows count data rows from 1, header not counted. A Word file is read by POST /api/lessons/read
// (YES-311) and checked here as rows; a CSV or TXT never leaves the browser before Analyse.

export const MAX_BYTES = 5 * 1024 * 1024;
export const SPEAKER_COLUMNS = ['speaker', 'spk', 'role', 'participant', 'actor'];
export const UTTERANCE_COLUMNS = ['utterance', 'text', 'transcript', 'message', 'content'];

// A file's text, with FileReader (File.text() is missing in older browsers and in jsdom)
export const readText = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
});

// Analysis speed on Railway, ~20 utterances a second (wf-teacher-upload-2 note)
export const secondsFor = (utterances) => Math.max(1, Math.round(utterances / 20));

// One CSV or TSV line set into rows of cells; quotes may hold the delimiter, quotes and newlines
export const parseDelimited = (text, delimiter) => {
    const rows = [];
    let row = [];
    let cell = '';
    let quoted = false;
    for (let i = 0; i < text.length; i += 1) {
        const ch = text[i];
        if (quoted) {
            if (ch === '"' && text[i + 1] === '"') { cell += '"'; i += 1; }
            else if (ch === '"') quoted = false;
            else cell += ch;
        } else if (ch === '"' && cell === '') {
            quoted = true;
        } else if (ch === delimiter) {
            row.push(cell); cell = '';
        } else if (ch === '\n' || ch === '\r') {
            if (ch === '\r' && text[i + 1] === '\n') i += 1;
            row.push(cell); rows.push(row); row = []; cell = '';
        } else {
            cell += ch;
        }
    }
    if (cell !== '' || row.length) { row.push(cell); rows.push(row); }
    return rows.filter((r) => r.some((c) => c.trim() !== ''));
};

// Study 1's speaker rule (r2609, src/adas/normalize.py, decision 2026-10-30), as the backend's
// services/lessons/fileCheck.js
const TEACHER_WORDS = ['teacher', 'tchr'];
const STUDENT_WORDS = ['s', 'ss', 'student', 'students', 'class', 'group', 'all', 'several students', 'many students',
    'male student', 'female student', 'male', 'female', 'boy', 'girl', 'male std', 'female std', 'male st', 'female st'];

// → { role: teacher | student | null, initials }: initials when one or two capitals other than T
// (optionally numbered) or a two-letter name such as Ty were read as a student. Case matters only there.
export const readSpeaker = (raw) => {
    const value = String(raw || '').trim().replace(/[:.\s]+$/, '');
    const low = value.toLowerCase();
    if (TEACHER_WORDS.includes(low) || /^t\d*$/.test(low) || /^(mr|mrs|ms|dr)(\.|\s|$)/.test(low)) return { role: 'teacher', initials: false };
    if (STUDENT_WORDS.includes(low) || /^(s|ss|students?)\s?\d+$/.test(low) || /^((male|female) )?student ?\d*$/.test(low)) {
        return { role: 'student', initials: false };
    }
    if (/^([A-Z]{1,2}\d?|[A-Z][a-z])$/.test(value)) return { role: 'student', initials: true };
    return { role: null, initials: false };
};

export const speakerRole = (raw) => readSpeaker(raw).role;

const ROLE_LABEL = { teacher: 'Teacher', student: 'Student' };

// → { ok, utterances, teacher, student, initials: [label], preview: [{ row, raw, role, text }], problems: [{ row, message }], fileError }
const checkRows = (columns, lines) => {
    const header = columns.map((h) => String(h).trim().toLowerCase());
    const speakerAt = header.findIndex((h) => SPEAKER_COLUMNS.includes(h));
    const textAt = header.findIndex((h) => UTTERANCE_COLUMNS.includes(h));
    if (speakerAt < 0 || textAt < 0) {
        return { ok: false, fileError: 'The file needs a speaker and an utterance column', problems: [] };
    }

    const problems = [];
    const read = [];
    const initials = new Set();
    lines.forEach((cells, i) => {
        const row = i + 1;
        const raw = String(cells[speakerAt] || '').trim();
        const utterance = String(cells[textAt] || '').trim();
        const speaker = readSpeaker(raw);
        if (!raw) problems.push({ row, message: 'The speaker is missing.' });
        else if (!speaker.role) problems.push({ row, message: `Speaker "${raw}" is not a teacher or a student.` });
        else if (!utterance) problems.push({ row, message: 'The utterance is empty.' });
        if (speaker.initials) initials.add(raw);
        read.push({ row, raw, role: speaker.role, roleLabel: ROLE_LABEL[speaker.role], text: utterance });
    });

    if (!read.length) return { ok: false, fileError: 'The file has no utterances', problems: [] };
    return {
        ok: problems.length === 0,
        problems,
        utterances: read.length,
        teacher: read.filter((r) => r.role === 'teacher').length,
        student: read.filter((r) => r.role === 'student').length,
        initials: [...initials],
        preview: read.slice(0, 5),
    };
};

export const checkTranscript = (text) => {
    // A TXT may be tab-separated: a header with a tab and no comma
    const firstLine = text.split(/\r?\n/, 1)[0];
    const delimiter = firstLine.includes('\t') && !firstLine.includes(',') ? '\t' : ',';
    const rows = parseDelimited(text.replace(/^\uFEFF/, ''), delimiter);
    return checkRows(rows[0] || [], rows.slice(1));
};

// read: POST /api/lessons/read's { layout, columns, rows }. In a paragraph transcript, lines before
// the first teacher or student turn whose label is neither (a title such as "Lesson 3: melting ice")
// are skipped, as the backend does.
export const checkDocx = (read) => {
    const columns = read.columns || [];
    const speakerAt = columns.indexOf('speaker');
    let lines = read.rows || [];
    if (read.layout === 'paragraphs') {
        const first = lines.findIndex((cells) => speakerRole(cells[speakerAt]));
        lines = first < 0 ? [] : lines.slice(first);
    }
    if (!lines.length) {
        return { ok: false, fileError: 'The Word file has no turns: it needs a transcript table or lines such as "T: text"', problems: [] };
    }
    return checkRows(columns, lines);
};
